/**
 * Purple nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const purpleNailDesignsPack: ToilePack = {
    slug: 'purple-nail-designs',
    category: 'beauty',
    name: 'Purple nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lavender', label: 'Lavender', subject: 'short square nails in soft lavender with a glossy finish'},
        {key: 'plum', label: 'Plum', subject: 'almond nails in a deep plum with a gold foil accent'},
        {key: 'ombre', label: 'Ombre', subject: 'coffin nails in a purple ombre from lilac to violet'},
    ],
    styles: [
        {key: 'spring', label: 'Spring', style: 'holding a sprig of lilac blossom, soft daylight from camera-left, a bright pastel balance with gentle shadow under the fingers', palette: 'Lilac flowers and fresh pale green leaves around the purple', framing: 'A near-frontal close view, the sprig crossing the lower third'},
        {key: 'autumn', label: 'Autumn', style: 'resting on a cream cable-knit sleeve in warm afternoon sun from camera-right, a cosy warm balance', palette: 'Cream wool and amber light, the purple deep against it', framing: 'Seen from above at a slight angle, the hand on the knit filling the frame'},
        {key: 'night', label: 'Night out', style: 'on an evening out, soft directional glow from camera-left, a dark moody balance with the nails the brightest point', palette: 'Near-black surroundings with faint violet reflections', framing: 'A near-frontal close view, the fingers on the clasp of a small black clutch'},
    ],
    directions: ['with glitter', 'french tip', 'dark purple'],
    directionHint: 'try: with glitter',
    seo: {
        title: 'Purple Nail Designs: Lavender, Plum, Ombre',
        metaDescription: 'Nine purple nail pictures: lavender, plum and purple ombre nails, shown with spring lilac, autumn knitwear and a night out.',
        h1: 'Purple nail designs: lavender, plum, ombre',
        tagline: 'Nine pictures of purple nails in three shades, each in three settings.',
        intro: 'Purple nails run from soft lavender to deep plum, and a purple ombre fades from lilac to violet. The nine pictures show short square lavender nails, almond plum nails with a gold foil accent and coffin nails in an ombre. Each shade appears with spring lilac, on a cream knit sleeve and on an evening out with a black clutch. Type a different finish or a twist, such as with glitter or french tip, and the same settings carry it.',
        useCases: [
            'a spring manicure with lilac',
            'an autumn nail colour for a cosy look',
            'a night-out nail idea',
            'a shade reference for a nail appointment',
        ],
        faq: [
            {q: 'What is the difference between the three purples?', a: 'Lavender is a soft, pale purple. Plum is deep, with a gold foil accent. The ombre fades from lilac to violet across the nail.'},
            {q: 'Which setting shows which look?', a: 'The spring setting shows lavender with lilac blossom, the autumn setting shows plum on a cream knit sleeve, and the night-out setting shows the ombre with a black clutch.'},
            {q: 'Can I ask for glitter or a French tip?', a: 'Yes. Type it as a request. The picture is not promised to match it exactly.'},
            {q: 'Are these photographs of real nails?', a: 'No. They are generated images of one hand in each setting, not photographs of a particular person.'},
        ],
    },
}
